// Vendor SDK — CRUD for outside-service / material suppliers.
//
// Mirrors src/shared/crm.ts shape (Customer / Part / Quote / Order SDK).
// Vendors are top-level entities at /vendors/{vendorId}, referenced by
// Part.outsideServices[].vendor and Part.materialCost.vendorId.
//
// Why this lives in its own module (not crm.ts):
//   - crm.ts is the original Module 5 entities. Vendor is adjacent — same
//     baseline shape and write conventions, but a different concern (the
//     vendor matrix lives in Settings, not the CRM nav).
//   - Splitting keeps crm.ts diffs noise-free when other agents touch
//     customer/part/quote/order code at the same time we're adding vendors.
//
// Helper duplication note:
//   The four helpers below (sanitizeForWrite, nowIso, makeAuditEntry,
//   newBaseline, readOne, readMany, readModifyWrite, writeSoftDeleteFlag)
//   are private copies of the originals in crm.ts. Per the vendor-rollout
//   spec we must not touch crm.ts beyond the one-line CRM_PATHS addition,
//   so we re-paste rather than refactor crm.ts to export them. If/when a
//   future cleanup pass lifts these into a shared `crmInternals.ts` module,
//   delete this block and import from there.

import {
  ref,
  push,
  set,
  get,
  query,
  orderByChild,
  equalTo,
  type DatabaseReference,
} from 'firebase/database';
import { db, awaitFirebaseUser, currentFirebaseUser } from './firebase';
import { CRM_PATHS } from './crm';
import type {
  Vendor,
  VendorServiceType,
  BaselineFields,
  AuditEntry,
} from './crmTypes';
import { stripUndefinedDeep, assertFiniteNumbers } from './crmValidators';

// =====================================================
// Private helpers — see "Helper duplication note" above
// =====================================================

function sanitizeForWrite(
  payload: Record<string, unknown>,
  contextLabel: string,
): Record<string, unknown> {
  const stripped = stripUndefinedDeep(payload, contextLabel);
  assertFiniteNumbers(stripped, contextLabel);
  return stripped as Record<string, unknown>;
}

function nowIso(): string {
  return new Date().toISOString();
}

function makeAuditEntry(
  action: AuditEntry['action'],
  extras: Partial<AuditEntry> = {},
): AuditEntry {
  return {
    ts: nowIso(),
    user: currentFirebaseUser()?.uid ?? 'system',
    action,
    source: 'ui',
    ...extras,
  };
}

function appendAudit(
  existing: AuditEntry[] | undefined,
  entry: AuditEntry,
): AuditEntry[] {
  return [...(existing ?? []), entry];
}

function newBaseline(): Omit<BaselineFields, 'id'> {
  const now = nowIso();
  return {
    createdAt: now,
    updatedAt: now,
    _v: 1,
    _audit: [makeAuditEntry('create')],
    _deleted: false,
  };
}

function withUpdateStamp<T extends Record<string, unknown>>(
  updates: T,
): T & { updatedAt: string } {
  return { ...updates, updatedAt: nowIso() };
}

async function readOne<T extends BaselineFields>(
  path: string,
  id: string,
  includeDeleted = false,
): Promise<T | null> {
  const snap = await get(ref(db, `${path}/${id}`));
  if (!snap.exists()) return null;
  const val = snap.val() as Omit<T, 'id'>;
  if (!includeDeleted && (val as { _deleted?: boolean })._deleted) return null;
  return { id, ...val } as T;
}

async function readMany<T extends BaselineFields>(
  path: string,
  opts: {
    orderBy?: string;
    equalToValue?: string | number | boolean;
    includeDeleted?: boolean;
  } = {},
): Promise<T[]> {
  const base = ref(db, path);
  let q;
  if (opts.orderBy && opts.equalToValue !== undefined) {
    q = query(base, orderByChild(opts.orderBy), equalTo(opts.equalToValue));
  } else if (opts.orderBy) {
    q = query(base, orderByChild(opts.orderBy));
  } else {
    q = base;
  }
  const snap = await get(q);
  if (!snap.exists()) return [];
  const out: T[] = [];
  snap.forEach((child) => {
    const val = child.val() as Omit<T, 'id'> & { _deleted?: boolean };
    if (!opts.includeDeleted && val._deleted) return false;
    out.push({ id: child.key!, ...val } as T);
    return false;
  });
  return out;
}

async function readModifyWrite<T extends BaselineFields>(
  path: string,
  id: string,
  entityLabel: string,
  updates: Record<string, unknown>,
): Promise<void> {
  const eRef = ref(db, `${path}/${id}`);
  const snap = await get(eRef);
  if (!snap.exists()) throw new Error(`${entityLabel} ${id} not found`);
  const existing = snap.val() as Omit<T, 'id'>;
  const auditEntry = makeAuditEntry('update', {
    metadata: { changedFields: Object.keys(updates) },
  });
  const merged = {
    ...existing,
    ...withUpdateStamp(updates),
    _audit: appendAudit((existing as { _audit?: AuditEntry[] })._audit, auditEntry),
  };
  await set(
    eRef,
    sanitizeForWrite(merged as Record<string, unknown>, `${entityLabel}.update`),
  );
}

async function writeSoftDeleteFlag<T extends BaselineFields>(
  path: string,
  id: string,
  entityLabel: string,
  deleted: boolean,
  reason?: string,
): Promise<void> {
  const eRef = ref(db, `${path}/${id}`);
  const snap = await get(eRef);
  if (!snap.exists()) throw new Error(`${entityLabel} ${id} not found`);
  const existing = snap.val() as Omit<T, 'id'>;
  const now = nowIso();
  const auditEntry = makeAuditEntry(deleted ? 'soft_delete' : 'restore', {
    metadata: reason ? { reason } : undefined,
  });

  const patch: Record<string, unknown> = {
    _deleted: deleted,
    updatedAt: now,
    _audit: appendAudit((existing as { _audit?: AuditEntry[] })._audit, auditEntry),
  };
  if (deleted) {
    patch._deletedAt = now;
    patch._deletedBy = currentFirebaseUser()?.uid ?? 'system';
    if (reason !== undefined) patch._deletedReason = reason;
  } else {
    patch._deletedAt = null;
    patch._deletedBy = null;
    patch._deletedReason = null;
  }

  const merged = { ...existing, ...patch };
  await set(
    eRef,
    sanitizeForWrite(
      merged as Record<string, unknown>,
      `${entityLabel}.${deleted ? 'softDelete' : 'restore'}`,
    ),
  );
}

// =====================================================
// Vendor CRUD
// =====================================================

function vendorsRef(): DatabaseReference {
  return ref(db, CRM_PATHS.vendors);
}

export type CreateVendorInput = Omit<Vendor, keyof BaselineFields>;
export type UpdateVendorInput = Partial<Omit<Vendor, keyof BaselineFields | 'id'>>;

export async function createVendor(input: CreateVendorInput): Promise<string> {
  await awaitFirebaseUser();
  // New items land at the bottom of whatever group they're in: compute
  // (max existing sortOrder + 100). If the caller already set sortOrder
  // (rare — only migration tooling), honor that.
  let sortOrder = input.sortOrder;
  if (sortOrder === undefined) {
    const existing = await readMany<Vendor>(CRM_PATHS.vendors, {});
    const max = existing.reduce(
      (acc, v) => (typeof v.sortOrder === 'number' && v.sortOrder > acc ? v.sortOrder : acc),
      0,
    );
    sortOrder = max + 100;
  }
  const newRef = push(vendorsRef());
  const id = newRef.key!;
  const record: Omit<Vendor, 'id'> = {
    ...newBaseline(),
    ...input,
    sortOrder,
  };
  await set(newRef, sanitizeForWrite(record as Record<string, unknown>, 'createVendor'));
  return id;
}

export async function getVendor(id: string): Promise<Vendor | null> {
  await awaitFirebaseUser();
  return readOne<Vendor>(CRM_PATHS.vendors, id);
}

export interface ListVendorsFilter {
  service?: VendorServiceType;
  favoriteOnly?: boolean;
  includeDeleted?: boolean;
}

export async function listVendors(filter: ListVendorsFilter = {}): Promise<Vendor[]> {
  await awaitFirebaseUser();
  // RTDB only supports one orderByChild per query — order by name and narrow
  // client-side. KMF will have dozens of vendors at most; client-filter is fine.
  const all = await readMany<Vendor>(CRM_PATHS.vendors, {
    orderBy: 'name',
    includeDeleted: filter.includeDeleted,
  });
  // Sort by sortOrder ascending (undefined → end), name as tiebreaker.
  // Items without sortOrder yet (legacy data pre-feature) fall to the bottom
  // of every group; Justin can re-order them with the ↑/↓ buttons.
  all.sort((a, b) => {
    const ao = typeof a.sortOrder === 'number' ? a.sortOrder : Number.POSITIVE_INFINITY;
    const bo = typeof b.sortOrder === 'number' ? b.sortOrder : Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    return a.name.localeCompare(b.name);
  });
  let out = all;
  if (filter.service) {
    out = out.filter((v) => v.services?.includes(filter.service!));
  }
  if (filter.favoriteOnly) {
    out = out.filter((v) => v.favorite === true);
  }
  return out;
}

/**
 * Swap the sortOrder of `id` with its neighbor in the requested direction
 * within the same tag group (or the untagged bucket when `tag` is undefined).
 * No-op at boundaries (top going up, bottom going down).
 *
 * Items missing sortOrder are treated as if they sit at the bottom of the
 * group (Number.POSITIVE_INFINITY) for sort purposes; when they need a real
 * number to swap with, we fall back to `(current + 100)` so the write is well-formed.
 */
export async function reorderInGroup(
  id: string,
  direction: 'up' | 'down',
  tag?: string,
): Promise<void> {
  await awaitFirebaseUser();
  const all = await listVendors();
  const inGroup = all.filter((v) => {
    if (tag === undefined) return !v.tags || v.tags.length === 0;
    return (v.tags ?? [])[0] === tag;
  });
  const idx = inGroup.findIndex((v) => v.id === id);
  if (idx === -1) return;
  const neighborIdx = direction === 'up' ? idx - 1 : idx + 1;
  if (neighborIdx < 0 || neighborIdx >= inGroup.length) return;
  const self = inGroup[idx];
  const neighbor = inGroup[neighborIdx];
  // Resolve concrete numeric sortOrders for both ends of the swap.
  const selfOrder =
    typeof self.sortOrder === 'number' ? self.sortOrder : (idx + 1) * 100;
  const neighborOrder =
    typeof neighbor.sortOrder === 'number'
      ? neighbor.sortOrder
      : (neighborIdx + 1) * 100;
  await Promise.all([
    updateVendor(self.id, { sortOrder: neighborOrder }),
    updateVendor(neighbor.id, { sortOrder: selfOrder }),
  ]);
}

export async function updateVendor(id: string, updates: UpdateVendorInput): Promise<void> {
  await awaitFirebaseUser();
  await readModifyWrite<Vendor>(
    CRM_PATHS.vendors,
    id,
    'Vendor',
    updates as Record<string, unknown>,
  );
}

export async function softDeleteVendor(id: string, reason?: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Vendor>(CRM_PATHS.vendors, id, 'Vendor', true, reason);
}

export async function restoreVendor(id: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Vendor>(CRM_PATHS.vendors, id, 'Vendor', false);
}

// =====================================================
// Seed suggestions — documentation only, NEVER auto-written.
//
// Justin types in his real vendors via the Settings UI. This list is for
// reference (what KMF currently uses for outside services) and so the Settings
// page can offer a "suggest one" hint when the list is empty. Do not import
// this into any code path that writes to Firebase.
// =====================================================

export const SEED_VENDORS_SUGGESTION: ReadonlyArray<{
  name: string;
  services: VendorServiceType[];
}> = [
  { name: 'Cleveland Laser Cutters', services: ['laser_cutting'] },
  { name: 'Akron Metal Plating', services: ['plating_zinc', 'plating_nickel', 'passivation'] },
  { name: 'Heat Treat USA — Macedonia', services: ['heat_treat'] },
  { name: 'Specialty Coating — Brunswick', services: ['painting', 'powder_coating'] },
  { name: 'Black Oxide Inc.', services: ['black_oxide'] },
];
